import React, { ReactElement, useMemo, useState } from 'react';
import { Box, Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import {
	AppThunkDispatch,
	BosatlasActions,
	bosatlasSearch,
	Bundle,
	MultiShareActions,
	multiShareOpenDialog
} from '../../../redux';

import {
	BosatlasBundleList,
	ComparisonAndProjectOverview,
	MultiShareDialog
} from '..';
import { ProjectDto, ProjectComparisonDto } from '../../../models';
import { Header2, Header3 } from '../../atoms';
import {
	ITextInputDialogProps,
	ISelectBundleDialogProps,
	TextInputDialog,
	SelectBundleDialog,
	BosatlasSearchField
} from '../../molecules';
import { connect } from 'react-redux';
import { useBrandName, useJwtToken } from '../../../hooks';

import './bosatlas-viewer.styles.scss';
import Share from '@material-ui/icons/Share';
import { useTranslation } from 'react-i18next';

export interface IBosatlasViewerProps {
	disabled?: boolean;
	bundles?: Bundle[];
	projects?: ProjectDto[];
	comparisons?: ProjectComparisonDto[];
	sharedProjects?: ProjectDto[];
	sharedComparisons?: ProjectComparisonDto[];
	textInputDialogState?: ITextInputDialogProps;
	selectBundleDialogState?: ISelectBundleDialogProps;
	disableEditing?: boolean | undefined;
	selectable?: boolean | undefined;
	sharing?: boolean | undefined;
	startBundleEdit?: (bundle?: Bundle) => void;
	onComparisonClick?: (comparison: ProjectComparisonDto) => void;
	onComparisonChangeBundle?: (comparison: ProjectComparisonDto) => void;
	onComparisonRemove?: (comparison: ProjectComparisonDto) => void;
	onComparisonUpdate?: (comparison: ProjectComparisonDto) => void;
	onProjectClick?: (project: ProjectDto) => void;
	onProjectChangeBundle?: (project: ProjectDto) => void;
	onProjectRemove?: (project: ProjectDto) => void;
	onProjectUpdate?: (project: ProjectDto) => void;
	isProjectSelected?: (project: ProjectDto) => boolean;
	isComparisonSelected?: (comparison: ProjectComparisonDto) => boolean;
	onProjectPreview?: (project: ProjectDto) => void;
	onViewBundleContentClick: (bundleId: string) => void;
}

export interface IBosatlasViewerStateProps {
	searchBosatlas: (searchValue: string) => void;
	openMultiShareDialog: () => void;
}

export function BosatlasViewerView(
	props: IBosatlasViewerProps & IBosatlasViewerStateProps
): ReactElement {
	const {
		disabled,
		bundles,
		projects,
		comparisons,
		sharedProjects,
		sharedComparisons,
		textInputDialogState,
		selectBundleDialogState,
		disableEditing,
		selectable,
		sharing,
		startBundleEdit,
		onComparisonClick,
		onComparisonChangeBundle,
		onComparisonRemove,
		onComparisonUpdate,
		onProjectClick,
		onProjectChangeBundle,
		onProjectRemove,
		onProjectUpdate,
		isProjectSelected,
		isComparisonSelected,
		searchBosatlas,
		onProjectPreview: onProjectSelect,
		onViewBundleContentClick,
		openMultiShareDialog
	} = props;
	const brandName: string = useBrandName();
	const { isTeacher } = useJwtToken();
	const { t } = useTranslation(['myBosatlas', 'general']);

	const [noBundlesString, setNoBundlesString] = useState<string>(
		t('myBosatlas:folders.noFolders')
	);

	const showSharedProjectsOverview: boolean = useMemo<boolean>(() => {
		if (isTeacher) return false;
		const hasProjects: boolean =
			sharedProjects !== undefined && sharedProjects.length > 0;
		const hasComparisons: boolean =
			sharedComparisons !== undefined && sharedComparisons.length > 0;

		return hasProjects || hasComparisons;
	}, [isTeacher, sharedProjects, sharedComparisons]);

	function performSearch(value: string) {
		searchBosatlas(value);
		setNoBundlesString(getNoFoundBundlesMessage(value));
	}

	function getNoFoundBundlesMessage(searchValue: string): string {
		if (searchValue === '') return t('myBosatlas:folders.noFolders');
		return t('myBosatlas:folders.noFolderSearch');
	}

	return (
		<Box className="bosatlas-viewer">
			<Box className="bosatlas-title-search">
				<Header2>
					{sharing && t('myBosatlas:chooseMaps')}{' '}
					{t('general:myAtlas', { brandName })}
				</Header2>
				<BosatlasSearchField onSearch={performSearch} />
			</Box>
			<Box className="bundles-header">
				<Header3>{t('myBosatlas:folders.titleFolders')}</Header3>
				{!disableEditing && startBundleEdit && (
					<Box className="actions">
						<Button
							color="primary"
							variant="outlined"
							onClick={() => startBundleEdit()}
						>
							<AddIcon />
							{t('myBosatlas:folders.createFolder')}
						</Button>
						{isTeacher && (
							<Button
								variant="contained"
								color="primary"
								onClick={openMultiShareDialog}
							>
								<Share />
								{t('myBosatlas:shareMaps')}
							</Button>
						)}
					</Box>
				)}
			</Box>
			{bundles && (
				<BosatlasBundleList
					bundles={bundles.slice(0, 6)}
					noBundlesMessage={noBundlesString}
					showAllButtonVisible={bundles.length > 5}
					disableEditing={disableEditing}
					startBundleEdit={startBundleEdit}
					onViewBundleContentClick={onViewBundleContentClick}
				/>
			)}
			{projects && comparisons && (
				<>
					<ComparisonAndProjectOverview
						disabled={disabled}
						projects={projects}
						comparisons={comparisons}
						sharedProjects={sharedProjects}
						sharedComparisons={sharedComparisons}
						selectable={selectable}
						disableEditing={disableEditing}
						isProjectSelected={isProjectSelected}
						isComparisonSelected={isComparisonSelected}
						onComparisonClick={onComparisonClick}
						onComparisonChangeBundle={onComparisonChangeBundle}
						onComparisonRemove={onComparisonRemove}
						onComparisonUpdate={onComparisonUpdate}
						onProjectClick={onProjectClick}
						onProjectChangeBundle={onProjectChangeBundle}
						onProjectRemove={onProjectRemove}
						onProjectUpdate={onProjectUpdate}
						onProjectPreview={onProjectSelect}
					/>
					{textInputDialogState && (
						<TextInputDialog {...textInputDialogState} />
					)}
				</>
			)}
			{showSharedProjectsOverview && (
				<ComparisonAndProjectOverview
					shared
					disabled={disabled}
					projects={sharedProjects ? sharedProjects : []}
					comparisons={sharedComparisons ? sharedComparisons : []}
					selectable={selectable}
					disableEditing={disableEditing}
					isProjectSelected={isProjectSelected}
					isComparisonSelected={isComparisonSelected}
					onComparisonClick={onComparisonClick}
					onComparisonChangeBundle={onComparisonChangeBundle}
					onComparisonRemove={onComparisonRemove}
					onComparisonUpdate={onComparisonUpdate}
					onProjectClick={onProjectClick}
					onProjectChangeBundle={onProjectChangeBundle}
					onProjectRemove={onProjectRemove}
					onProjectUpdate={onProjectUpdate}
					onProjectPreview={onProjectSelect}
				/>
			)}
			{selectBundleDialogState && (
				<SelectBundleDialog {...selectBundleDialogState} />
			)}
			{!selectable && <MultiShareDialog />}
		</Box>
	);
}

function mapDispatchToProps(
	dispatch: AppThunkDispatch<BosatlasActions | MultiShareActions>
) {
	return {
		searchBosatlas: (searchValue: string) =>
			dispatch(bosatlasSearch(searchValue)),
		openMultiShareDialog: () => dispatch(multiShareOpenDialog())
	};
}

export const BosatlasViewer = connect(
	undefined,
	mapDispatchToProps
)(BosatlasViewerView);
